import { useCallback, useEffect, useRef } from 'react';
import { useEnvironment } from '@wix/yoshi-flow-editor';
import {
  wixBlogPostPageReadingEndSrc69Evid449,
  wixBlogPostPageReadingStartSrc69Evid448,
} from '@wix/bi-logger-blog-data/v2';
import { biWithBeaconSupport } from '../../services/bi-with-beacon';
import { useOnChange } from './use-on-change';

type Props = {
  postId: string;
};

const PostPageReadingTimeBiElementV2: React.FC<Props> = ({ postId }) => {
  const { isSSR, isEditor } = useEnvironment();

  const isMountedRef = useRef(false);
  const isEnabled = !isSSR && !isEditor;

  const lastReportedArgs = useRef<{ isContentVisible: boolean; postId: string } | null>(null);

  const reportEvent = useCallback((isContentVisible: boolean, post_stable_id: string) => {
    if (
      lastReportedArgs.current?.postId === post_stable_id &&
      lastReportedArgs.current?.isContentVisible === isContentVisible
    ) {
      return;
    }

    lastReportedArgs.current = {
      isContentVisible,
      postId: post_stable_id,
    };

    biWithBeaconSupport.report(
      isContentVisible
        ? wixBlogPostPageReadingStartSrc69Evid448({ post_stable_id })
        : wixBlogPostPageReadingEndSrc69Evid449({ post_stable_id }),
    );
  }, []);

  useEffect(() => {
    if (!isEnabled || isMountedRef.current) {
      return;
    }
    // Initial
    reportEvent(true, postId);
    isMountedRef.current = true;
  }, [isEnabled, reportEvent, postId]);

  useEffect(() => {
    if (!isEnabled || !isMountedRef.current) {
      return;
    }

    const handleBeforeUnload = () => {
      // Tab closed, navigated to a different page or refreshed
      reportEvent(false, postId);
    };

    const handleVisibilityChange = () => {
      // Tab switched to background or foreground
      reportEvent(document.visibilityState === 'visible', postId);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isEnabled, reportEvent, postId]);

  const handleIdChange = useCallback(
    (oldPostId: string, newPostId: string) => {
      // Client-side navigation occurred
      if (isEnabled && isMountedRef.current) {
        reportEvent(false, oldPostId);
        reportEvent(true, newPostId);
      }
    },
    [isEnabled, reportEvent],
  );
  useOnChange(postId, handleIdChange);

  return null;
};

export default PostPageReadingTimeBiElementV2;
